import { Dialog } from "@mui/material";
import { useScale, formatDateTime } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/parent/i18n";
import { UserActivityLog, UserActivityType, CourseClearActivityData } from "features/parent/types";

interface ActivityListModalProps {
  activities: UserActivityLog[];
  onClose: () => void;
}

// 活動タイプに応じたアイコンクラス
const getActivityIconClass = (type: string): string => {
  switch (type) {
    case UserActivityType.COURSE_LEVEL_CLEAR:
      return "bg-[rgba(255,191,0,0.15)] text-orange-100";
    case UserActivityType.PROJECT_CREATED:
      return "bg-[rgba(76,157,241,0.15)] text-blue-100";
    case UserActivityType.PROJECT_PUBLISHED:
      return "bg-[rgba(43,220,152,0.15)] text-green-100";
    case UserActivityType.PROJECT_FAVORITED:
      return "bg-[rgba(252,101,96,0.15)] text-pink-100";
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return "bg-[rgba(252,101,96,0.15)] text-danger";
    case UserActivityType.LOGIN:
      return "bg-[rgba(20,184,166,0.15)] text-teal-600";
    case UserActivityType.ICON_CHANGED:
      return "bg-[rgba(147,51,234,0.15)] text-purple-100";
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return "bg-[rgba(6,182,212,0.15)] text-cyan-100";
    case UserActivityType.INQUIRY_SENT:
      return "bg-green-50 text-green-700";
    case UserActivityType.APP_REVIEWED:
      return "bg-yellow-100 text-yellow-700";
    case UserActivityType.FEEDBACK_SENT:
      return "bg-blue-50 text-blue-700";
    default:
      return "bg-[rgba(76,157,241,0.15)] text-blue-100";
  }
};

// 活動タイプに応じたアイコン
const getActivityIcon = (type: string): string => {
  switch (type) {
    case UserActivityType.COURSE_LEVEL_CLEAR:
      return "emoji_events";
    case UserActivityType.PROJECT_CREATED:
      return "palette";
    case UserActivityType.PROJECT_PUBLISHED:
      return "public";
    case UserActivityType.PROJECT_FAVORITED:
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return "favorite";
    case UserActivityType.LOGIN:
      return "login";
    case UserActivityType.ICON_CHANGED:
      return "face";
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return "smart_toy";
    case UserActivityType.INQUIRY_SENT:
      return "mail";
    case UserActivityType.APP_REVIEWED:
      return "star";
    case UserActivityType.FEEDBACK_SENT:
      return "feedback";
    default:
      return "info";
  }
};

// 活動タイトルの生成
const getActivityTitle = (activity: UserActivityLog): string => {
  const data = activity.activity_data;

  switch (activity.activity_type) {
    case UserActivityType.COURSE_LEVEL_CLEAR: {
      const courseData = data as CourseClearActivityData;
      const courseNames: Record<string, string> = {
        algorithm: i18n.t("MSG_COURSE_ALGORITHM"),
        algorithm_tangible: i18n.t("MSG_COURSE_ALGORITHM"),
        program: i18n.t("MSG_COURSE_PROGRAM"),
        algorithm_advanced: i18n.t("MSG_COURSE_ALGORITHM_ADVANCED"),
      };
      const courseName = courseNames[courseData?.course_type] || "";
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_COURSE_CLEAR", {
        course: courseName,
        stage: courseData?.stage_id ?? 0,
        level: courseData?.level_id ?? 0,
      });
    }
    case UserActivityType.PROJECT_CREATED: {
      const projectData = data as { project_name?: string };
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_CREATED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_PUBLISHED: {
      const projectData = data as { project_name?: string };
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_PUBLISHED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_FAVORITED: {
      const projectData = data as { project_name?: string };
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_FAVORITED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_FAVORITED_RECEIVED");
    case UserActivityType.LOGIN:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_LOGIN");
    case UserActivityType.ICON_CHANGED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_ICON_CHANGED");
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_AI_CHAT");
    case UserActivityType.INQUIRY_SENT:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_INQUIRY_SENT");
    case UserActivityType.APP_REVIEWED: {
      const reviewData = data as { rating?: number };
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_APP_REVIEWED", { rating: reviewData?.rating ?? 0 });
    }
    case UserActivityType.FEEDBACK_SENT:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_FEEDBACK_SENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_DEFAULT");
  }
};

export const ActivityListModal = ({ activities, onClose }: ActivityListModalProps) => {
  const { scale } = useScale();

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[600px] h-[700px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white overflow-hidden">
              <ParentCloseBtn close={onClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">history</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_RECENT_ACTIVITY_24H")}
                </h2>
                <span className="text-[24px] text-gray2-100 ml-1">({activities.length})</span>
              </div>

              {/* 活動リスト */}
              <div className="flex-1 overflow-y-auto invisible-scrollbar min-h-0">
                <div className="flex-col-el">
                  {activities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className={`flex-row-el gap-3 py-3 ${
                        index < activities.length - 1 ? "border-b border-outline" : ""
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 ${getActivityIconClass(activity.activity_type)}`}
                      >
                        <span className="material-symbols-outlined text-[30px]">
                          {getActivityIcon(activity.activity_type)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[21px] text-textcolor-black mb-0.5">
                          {getActivityTitle(activity)}
                        </div>
                        <div className="text-[18px] text-gray2-100">
                          {formatDateTime(activity.activity_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
